import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://askkimberley.com";
const SITE_NAME = "Ask Kimberley";
const DEFAULT_DESCRIPTION =
  "Canine expert meets smart products and digital support. A new kind of dog brand — New Zealand-sourced natural products, practical canine expertise and personalised digital support.";
const DEFAULT_OG_IMAGE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_bark-help/artifacts/5qb3gapy_ChatGPT%20Image%20Sep%2010%2C%202026%2C%2011_06_20%20AM.png";

/**
 * Per-page SEO. Pass title, description, path (relative, starts with /),
 * optional image (absolute URL). Falls back to sensible defaults.
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Robots + language */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <html lang="en" />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Ask Kimberley",
  alternateName: "Ask Kimberley for Dog PPL",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  image: DEFAULT_OG_IMAGE,
  description: DEFAULT_DESCRIPTION,
  founder: {
    "@type": "Person",
    name: "Kimberley",
    jobTitle: "Founder",
    knowsAbout: [
      "Canine behavior",
      "Pet industry strategy",
      "Product development",
      "Dog business operations",
      "Brand strategy",
    ],
  },
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "Australia" },
    { "@type": "Country", name: "New Zealand" },
  ],
  sameAs: [
    "https://instagram.com/askkimberley.dog",
    "https://tiktok.com/@askkimberley.dog",
  ],
};

export const PERSON_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Kimberley",
  jobTitle: "Founder, Ask Kimberley",
  url: SITE_URL,
  image: DEFAULT_OG_IMAGE,
  worksFor: {
    "@type": "Organization",
    name: "Ask Kimberley",
    url: SITE_URL,
  },
  description:
    "Kimberley is the founder of Ask Kimberley, with 30+ years with dogs across the USA, Australia and New Zealand — from U.S. Air Force dog handling to pet product development, brand and commercial operations. Now building a new kind of dog brand: natural products, canine expertise and digital support.",
  knowsAbout: [
    "Canine behavior",
    "Pet industry strategy",
    "Product development",
    "Dog business operations",
    "Brand strategy",
    "AI in pet tech",
  ],
  sameAs: [
    "https://instagram.com/askkimberley.dog",
    "https://tiktok.com/@askkimberley.dog",
  ],
};
